<div class="flex flex-col gap-4">
  <div class="flex items-center justify-between">
    <h2 class="text-xl font-medium leading-8 tracking-wide text-gray-900 m-0">
      Secrets
    </h2>
    <button mat-button class="add-button" (click)="onAddNew()">
      <mat-icon>add_circle</mat-icon>
      <span>Add New</span>
    </button>
  </div>

  <!-- Error message (other than no secret sources; that case switches to Secret Sources tab) -->
  @if (addFormError() && !showAddForm()) {
    <div class="error-message">
      <mat-icon>error</mat-icon>
      <span>{{ addFormError() }}</span>
    </div>
  }

  <!-- Action error (e.g. a failed delete) -->
  @if (actionError()) {
    <div class="error-message">
      <mat-icon>error</mat-icon>
      <span>{{ actionError() }}</span>
    </div>
  }

  <!-- Inline Add Form -->
  @if (showAddForm()) {
    <div class="inline-add-form">
      <div class="form-header">
        <h3>New Secret</h3>
        <div class="form-actions">
          <button
            mat-button
            (click)="onCancelAdd()"
            [disabled]="addFormLoading()"
            class="cancel-button"
          >
            <span>Cancel</span>
          </button>
          <button
            mat-button
            (click)="onSaveAdd()"
            [disabled]="addFormLoading() || addForm.invalid"
            class="add-button"
          >
            @if (addFormLoading()) {
              <mat-spinner diameter="20"></mat-spinner>
            }
            @if (!addFormLoading()) {
              <mat-icon>add</mat-icon>
            }
            @if (!addFormLoading()) {
              <span>Add</span>
            }
          </button>
        </div>
      </div>
      <form [formGroup]="addForm" (ngSubmit)="onSaveAdd()">
        <!-- Error Message -->
        @if (addFormError()) {
          <div class="add-form-error">
            <mat-icon>error</mat-icon>
            <span>{{ addFormError() }}</span>
          </div>
        }
        <div class="form-grid">
          <!-- Secret Name -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Secret Name</mat-label>
              <input
                matInput
                formControlName="name"
                placeholder="Secret Name"
              />
            </mat-form-field>
            @if (getAddFormFieldError("name")) {
              <div class="validation-error">
                {{ getAddFormFieldError("name") }}
              </div>
            }
          </div>
          <!-- Type/Source Selection -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Source</mat-label>
              <mat-select
                formControlName="sourceId"
                placeholder="Choose Source"
                (selectionChange)="onAddSourceChange()"
              >
                @for (source of secretSources(); track source.sourceId) {
                  <mat-option [value]="source.sourceId">
                    {{ source.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (getAddFormFieldError("sourceId")) {
              <div class="validation-error">
                {{ getAddFormFieldError("sourceId") }}
              </div>
            }
          </div>
          <!-- Secret Value (a reference/path for Vault/GCP sources) -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>{{ valueLabel(addSourceType()) }}</mat-label>
              <input
                matInput
                [type]="
                  isAddPointerSource()
                    ? 'text'
                    : hideValue()
                      ? 'password'
                      : 'text'
                "
                formControlName="value"
                [placeholder]="valueLabel(addSourceType())"
              />
              @if (!isAddPointerSource()) {
                <button
                  mat-icon-button
                  matSuffix
                  (click)="hideValue.set(!hideValue())"
                  type="button"
                  tabindex="-1"
                >
                  <mat-icon>{{
                    hideValue() ? "visibility_off" : "visibility"
                  }}</mat-icon>
                </button>
              }
              @if (isAddPointerSource()) {
                <mat-hint>{{ valueHint(addSourceType()) }}</mat-hint>
              }
            </mat-form-field>
            @if (getAddFormFieldError("value")) {
              <div class="validation-error">
                {{ getAddFormFieldError("value") }}
              </div>
            }
          </div>
          <!-- Gettable Checkbox — admin's per-secret choice, all source types -->
          <div class="field-container checkbox-container">
            <mat-checkbox formControlName="gettable">Gettable</mat-checkbox>
          </div>
        </div>
      </form>
    </div>
  }

  <!-- Inline Edit Form -->
  @if (showEditForm()) {
    <div class="inline-edit-form">
      <div class="form-header">
        <h3>Edit Secret</h3>
        <div class="form-actions">
          <button
            mat-button
            (click)="onCancelEdit()"
            [disabled]="editFormLoading()"
            class="cancel-button"
          >
            <span>Cancel</span>
          </button>
          <button
            mat-button
            (click)="onSaveEdit()"
            [disabled]="editFormLoading() || editForm.invalid"
            class="save-button"
          >
            @if (editFormLoading()) {
              <mat-spinner diameter="20"></mat-spinner>
            }
            @if (!editFormLoading()) {
              <mat-icon>save</mat-icon>
            }
            @if (!editFormLoading()) {
              <span>Save</span>
            }
          </button>
        </div>
      </div>
      <form [formGroup]="editForm" (ngSubmit)="onSaveEdit()">
        <!-- Error Message -->
        @if (editFormError()) {
          <div class="edit-form-error">
            <mat-icon>error</mat-icon>
            <span>{{ editFormError() }}</span>
          </div>
        }
        <div class="form-grid">
          <!-- Secret Name -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Secret Name</mat-label>
              <input
                matInput
                formControlName="name"
                placeholder="Secret Name"
              />
            </mat-form-field>
            @if (getEditFormFieldError("name")) {
              <div class="validation-error">
                {{ getEditFormFieldError("name") }}
              </div>
            }
          </div>
          <!-- Type/Source Selection -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>Source</mat-label>
              <mat-select
                formControlName="sourceId"
                placeholder="Choose Source"
              >
                @for (source of secretSources(); track source.sourceId) {
                  <mat-option [value]="source.sourceId">
                    {{ source.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <div class="text-xs text-gray-500 mt-1">
              Secret source cannot be changed in edit mode.
            </div>
          </div>
          <!-- Secret Value (a reference/path for Vault/GCP sources) -->
          <div class="field-container">
            <mat-form-field appearance="fill">
              <mat-label>{{ valueLabel(editSourceType()) }}</mat-label>
              <input
                matInput
                [type]="
                  isEditPointerSource()
                    ? 'text'
                    : hideEditValue()
                      ? 'password'
                      : 'text'
                "
                formControlName="value"
                placeholder="Enter new value (leave blank to keep current)"
              />
              @if (!isEditPointerSource()) {
                <button
                  mat-icon-button
                  matSuffix
                  (click)="hideEditValue.set(!hideEditValue())"
                  type="button"
                  tabindex="-1"
                >
                  <mat-icon>{{
                    hideEditValue() ? "visibility_off" : "visibility"
                  }}</mat-icon>
                </button>
              }
              @if (isEditPointerSource()) {
                <mat-hint>{{ valueHint(editSourceType()) }}</mat-hint>
              }
            </mat-form-field>
            @if (getEditFormFieldError("value")) {
              <div class="validation-error">
                {{ getEditFormFieldError("value") }}
              </div>
            }
          </div>
          <!-- Gettable Checkbox — admin's per-secret choice, all source types -->
          <div class="field-container checkbox-container">
            <mat-checkbox formControlName="gettable">Gettable</mat-checkbox>
          </div>
        </div>
      </form>
    </div>
  }

  <mat-card
    class="border border-blue-100 rounded-lg shadow-sm overflow-hidden p-0"
  >
    @if (loading()) {
      <div class="flex justify-center items-center py-10">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
    }

    <!-- Empty State -->
    @if (!loading() && displaySecrets().length === 0) {
      <div class="flex flex-col justify-center items-center py-16 gap-4">
        <mat-icon class="text-gray-400 text-6xl">security</mat-icon>
        <h3 class="text-gray-600 text-lg font-medium m-0">No secrets found</h3>
        <p class="text-gray-500 text-sm m-0 text-center max-w-sm">
          Store and manage your sensitive data securely with encrypted secrets.
        </p>
      </div>
    }

    @if (!loading() && displaySecrets().length > 0) {
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        <!-- Secret Name Column -->
        <ng-container matColumnDef="name">
          <th
            mat-header-cell
            *matHeaderCellDef
            mat-sort-header
            class="table-header"
          >
            Secret Name
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            {{ element.name }}
          </td>
        </ng-container>
        <!-- Source Column -->
        <ng-container matColumnDef="sourceName">
          <th
            mat-header-cell
            *matHeaderCellDef
            mat-sort-header
            class="table-header"
          >
            Source
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell">
            {{ element.sourceName }}
          </td>
        </ng-container>
        <!-- Security Status Column -->
        <ng-container matColumnDef="gettable">
          <th mat-header-cell *matHeaderCellDef class="table-header-center">
            Security
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell-center">
            <div
              class="security-badge"
              [class.gettable]="element.gettable"
              [class.write-only]="!element.gettable"
            >
              <mat-icon>{{ element.securityIcon }}</mat-icon>
              <span>{{ element.securityLabel }}</span>
            </div>
          </td>
        </ng-container>
        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-header-center">
            Actions
          </th>
          <td mat-cell *matCellDef="let element" class="table-cell-center">
            <button
              mat-icon-button
              [matMenuTriggerFor]="secretMenu"
              class="action-menu-button"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #secretMenu="matMenu">
              <button
                mat-menu-item
                (click)="onActionMenu(element.original, 'view')"
                [disabled]="!element.gettable"
              >
                <mat-icon>visibility</mat-icon>
                <span>View Value</span>
              </button>
              <button
                mat-menu-item
                (click)="onActionMenu(element.original, 'edit')"
              >
                <mat-icon>edit</mat-icon>
                <span>Edit</span>
              </button>
              <button
                mat-menu-item
                (click)="onActionMenu(element.original, 'delete')"
              >
                <mat-icon>delete</mat-icon>
                <span>Delete</span>
              </button>
            </mat-menu>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="data-row"
          [class.editing]="editingSecret()?.secretId === row.secretId"
        ></tr>
      </table>
    }
  </mat-card>
</div>
